import { Subject } from 'rxjs';

import { CmsPageSection, PageTemplate, Suburb, SuburbBlock } from '../../../../models/content';
import { SuburbPageRequest, suburbPageRequest } from '../../block-preview/block-preview-request';
import { BlockPreviewSource } from '../../block-preview/block-preview-source';
import { StagePreviewService } from '../workspace/stage-preview.service';
import { addBlock, blocksForSave, replaceBlock } from './page-structure';
import { applyPageTab, PageTabValues } from './page-tab';

/** What the Page tab holds now (#262), for its previews (#263). */
export interface PageTabDraft {
  record: Suburb;
  values: PageTabValues;
  template: PageTemplate;
  blocks: SuburbBlock[];
  fromMap: boolean;
  live: boolean;
}

/** The whole page as the tab holds it: the record as Save would write it (applyPageTab), no focus. */
export function pageTabRequest(d: PageTabDraft): SuburbPageRequest | null {
  return suburbPageRequest(d.record._meta.id, unsavedRecord(d, d.blocks), null, null);
}

/**
 * One block being edited on the page (new, placed after `after`; or `after` null, replacing its saved self), drawn on
 * its own (focus) where Done would put it.
 */
export function insertedBlockRequest(
  d: PageTabDraft,
  section: CmsPageSection,
  after: string | null
): SuburbPageRequest | null {
  const blocks =
    after !== null ? addBlock(d.blocks, after, section) : replaceBlock(d.blocks, section);
  return suburbPageRequest(d.record._meta.id, unsavedRecord(d, blocks), null, section._meta.id);
}

function unsavedRecord(d: PageTabDraft, blocks: SuburbBlock[]): Suburb {
  return applyPageTab(d.record, {
    original: d.record,
    values: d.values,
    blocks: blocksForSave(d.template, blocks),
    fromMap: d.fromMap,
    live: d.live,
    published: null,
  });
}

/**
 * The Page tab's preview on the Suburbs stage: "Preview page" shows the whole page and follows the tab's edits; a
 * block being edited in the tab shows on its own (insertedBlockRequest, from the block editor).
 */
export class PageTabPreview {
  readonly source: BlockPreviewSource;
  private readonly edits$ = new Subject<void>();

  constructor(
    private stage: StagePreviewService,
    private draft: () => PageTabDraft
  ) {
    this.source = { changes$: this.edits$, current: () => pageTabRequest(this.draft()) };
  }

  /** A field or the blocks changed. */
  changed(): void {
    this.edits$.next();
  }

  /** "Preview page": the whole page on the stage, and Preview on. */
  show(): void {
    this.stage.show(this.source, true);
  }

  block(section: CmsPageSection, after: string | null): SuburbPageRequest | null {
    return insertedBlockRequest(this.draft(), section, after);
  }

  /** The tab closed. */
  dispose(): void {
    this.stage.release(this.source);
    this.edits$.complete();
  }
}
